import {Component, inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {SharedModule} from '../../shared/shared.module';
import {
  ButtonSize,
  ButtonState,
  ButtonType, IEducation, ILanguage,
  InputSize,
  InputState,
  IWorkExperience,
} from '../../../assets/types/dtoTypes';
import {TranslateModule} from '@ngx-translate/core';
import {MatChip, MatChipRemove, MatChipSet} from '@angular/material/chips';
import {MatIcon} from '@angular/material/icon';
import {map, startWith} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {LanguageDataService} from "../../shared/services/language-data.service";
import {ButtonComponent} from "../../shared/button/button.component";
import {AppInputAreaComponent} from "../../shared/text-areas/app-input-area.component";
import {AzureService} from "../../shared/services/azure.service";
import {ImageService} from "../../shared/services/image.service";

@Component({
  selector: 'app-cv-result-modal',
  standalone: true,
  imports: [
    FormsModule,
    SharedModule,
    TranslateModule,
    MatChip,
    MatChipRemove,
    MatChipSet,
    MatIcon,
    ReactiveFormsModule,
    ButtonComponent,
    AppInputAreaComponent,
  ],
  templateUrl: './cv-result-modal.component.html',
  styleUrl: './cv-result-modal.component.scss'
})
export class CvResultModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CvResultModalComponent>>(MatDialogRef);
  private dataService = inject(LanguageDataService);
  private azureService = inject(AzureService);
  private imageService = inject(ImageService);
  data = inject(MAT_DIALOG_DATA);
  showPersonalInfoStep = false;


  countries = [];

  userId;

  education: IEducation[] = [];

  workExperience: IWorkExperience[] = [];

  languages: ILanguage[] = [];

  languageCtrl = new FormControl();

  filteredLanguages!: Observable<ILanguage[]>;

  selectedLanguages: string[] = [];

  steps = ['Bio', 'Work Experience', 'Education', 'Language'];

  currentStep = 0;

  constructor() {
    const data = this.data;
    const isEdit = data?.source === 'edit-candidate';
    this.showPersonalInfoStep = !isEdit;

    this.showPersonalInfoStep = data?.source === 'add-new-candidate';
    if (this.showPersonalInfoStep) {
      this.userId = data?.userId;
    }

    this.steps = this.showPersonalInfoStep
      ? ['Personal Information', 'Bio', 'Work Experience', 'Education', 'Language']
      : ['Bio', 'Work Experience', 'Education', 'Language'];

    this.workExperience = data?.workExperience?.map(job => ({
      ...job,
      displayStartDate: this.formatToMonth(job.startDate),
      displayCompletionDate: this.formatToMonth(job.completionDate),
    })) ?? [];

    this.education = data?.education?.map(edu => ({
      ...edu,
      displayStartDate: this.formatToMonth(edu.startDate),
      displayCompletionDate: this.formatToMonth(edu.completionDate),
    })) ?? [];


    this.selectedLanguages = data?.languages ?? [];


    this.dataService.getLanguages().subscribe((returnData) => {
      this.languages = returnData.languages;
      this.filteredLanguages = this.languageCtrl.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? this._filter(value) : this.languages.slice()),
      );
    });
  }

  ngOnInit() {
    this.dataService.getCountries().subscribe(countries => {
      this.countries = countries;
    });
    this.languageCtrl.valueChanges.subscribe(vals => {
      this.selectedLanguages = vals ?? [];
    });
  }

  private _filter(value: string): ILanguage[] {
    const filterValue = value.toLowerCase();
    return this.languages.filter(language => language.name.toLowerCase().includes(filterValue));
  }


  private formatToMonth(dateStr: string | null): string | null {
    if (!dateStr) return null;
    const [year, month] = dateStr.split('-');
    return year && month ? `${year}-${month}` : null;
  }

  goNextStep() {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
    }
  }

  goPreviousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  addEducation() {
    this.education.push({
      organization: '',
      education: '',
      educationLevel: '',
      location: '',
      startDate: '',
      completionDate: '',
      isCurrent: false,
    });
  }

  addWorkExperience() {
    this.workExperience.push({
      jobTitle: '',
      organization: '',
      location: '',
      startDate: '',
      completionDate: '',
      jobDescription: '',
      isCurrent: false,
    });
  }

  removeItem(array: any[], index: number) {
    if (Array.isArray(array)) {
      array.splice(index, 1);
    }
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onSave() {
    const normalizedWorkExperience = this.workExperience.map(exp => ({
      ...exp,
      startDate: this.toIsoDate(exp.startDate),
      completionDate: this.toIsoDate(exp.completionDate),
    }));

    const normalizedEducation = this.education.map(edu => ({
      ...edu,
      startDate: this.toIsoDate(edu.startDate),
      completionDate: this.toIsoDate(edu.completionDate),
    }));

    const result: any = {
      motivation: this.data.bio,
      education: normalizedEducation,
      workExperience: normalizedWorkExperience,
      languages: this.selectedLanguages,
    };

    if (this.showPersonalInfoStep) {
      result.location = this.selectedCountry;
      result.profileImageUrl = this.profileImagePreviewUrl;
    }

    this.dialogRef.close(result);
  }


  toIsoDate(dateStr: string): string {
    if (!dateStr) return '';

    const parts = dateStr.split('-');
    if (parts.length === 2) {
      return `${parts[0]}-${parts[1]}-01`;
    }

    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return '';

    return parsed.toISOString().split('T')[0];
  }

  removeLanguage( language: string ): void {
    const index = this.selectedLanguages.indexOf( language );
    if ( index >= 0 ) {
      this.selectedLanguages.splice( index, 1 );
      this.languageCtrl.setValue( this.selectedLanguages );
    }
  }

  setImageFile(file: File) {
    this.azureService.updateProfileImage(this.userId, file).subscribe((user) => {
      this.profileImagePreviewUrl = user.profileImageUrl;
      this.imageService.appendSasTokenToProfileImage( user ).subscribe( updated => {
        this.profileImagePreviewUrl = updated.profileImageUrl!;
      } );
    });
  }
  selectedCountry: string = '';
  profileImagePreviewUrl: string = '';


  getFlagSrc(languageName: string): string {
    const lang = this.languages?.find(l => l.name === languageName);
    const code = lang?.code ?? 'default';
    return `assets/icons/flags/square/${code.toLowerCase()}.svg`;
  }

  protected readonly ButtonType = ButtonType;

  protected readonly ButtonState = ButtonState;

  protected readonly ButtonSize = ButtonSize;

  protected readonly InputSize = InputSize;

  protected readonly InputState = InputState;
}

